<template>
	<div class="container teacher-container">
		<header class="d-flex justify-content-between align-items-center">
			<breadcrumb v-if="breadcrumbTo" :to="breadcrumbTo" :useOffset="false" id="header-breadcrumb" />
			<img src="~@img/misc/lineal_logo.png"/>
			<fullscreen-toggle-button id="fullscreen-toggle-button"/>
		</header>
		<transition :name="componentTransition">
			<component :is="currentPage" class="teacher-page"></component>
		</transition>
		<emoji-code class="room-code" type="tiny" v-if="currentPage == 'pages'"/>

        <gdpr-cookie-toggler
            locale="da"
            :cookies-groups="cookiesGroups"
            @activeCategoriesChanged="onCookieTogglerActiveCategoriesChanged"
        />
	</div>
</template>

<script>
    import * as extend from 'extend'

    import ConnectPage from './flow/0-connect'
    import IntroPage from './flow/1-intro'
    import SelectThemePage from './flow/2-select-theme'
    import SelectCasePage from './flow/3-select-case'
    import LobbyPage from './flow/4-lobby'
    import PageController from './flow/5-page-controller'

    import FullscreenToggleButton from '@/components/fullscreen-toggle-button'
    import EmojiCode from '@/components/emoji-code'
    import GameClientEvent from '@/enum/GameClientEvent'
    import GamePhase from '@/enum/GamePhase'
    import GameType from '@/enum/GameType'
    import GameClientType from '@/enum/GameClientType'
    import {ConsoleLogger} from '@/console-logger'
    import {$eBus} from '@/event-bus'
    import AppConfig from '@/app-config'
    import {mapGetters} from 'vuex'

    import * as MutationTypes from '@/store/mutation-types'
    import {getTeacherUrl} from '@/utils/UrlUtil'
	import Breadcrumb from '@/components/breadcrumb'
    import GdprCookieToggler from "@/components/app-components/gdpr-cookie-toggler";

    const pageComponents = {
		'connect': ConnectPage,
		'intro': IntroPage,
		'select-theme': SelectThemePage,
		'select-case': SelectCasePage,
		'lobby': LobbyPage,
		'pages': PageController
	}

	const components = {
		Breadcrumb,
		EmojiCode,
		FullscreenToggleButton,
        GdprCookieToggler
	}

	export default {
		components: extend(components, pageComponents),
		data() {
			return {
				componentTransition: AppConfig.componentTransition,
				currentPage: 'connect',
				logger: new ConsoleLogger('TeacherController', 'background: #00aaaa; color: white', null,
					!AppConfig.debug.teacherController),

                cookiesGroups: [
                    {
                        category: 'analytics',
                        name: 'Statistiske',
                        description: 'Brugt til at se anonymt brug af siden for at optimere løsningen.',
                        active: false,
                    },
                ],
			}
		},
		watch: {
			'$route'(to, from) {
				this.reloadCurrentPage(to)
			}
		},
		computed: {
			...mapGetters({
				breadcrumbTo: 'getBreadcrumbTo',
			}),
		},
		methods: {
            onCookieTogglerActiveCategoriesChanged(activeCategories) {
                if (activeCategories.includes('analytics')) {
                    console.log('We accepted analytics, so opting in to google analytics');

                    this.$gaAnalytics.setGTag(this.$gtag);
                    this.$gaAnalytics.enable();
                } else {
                    console.log('Not opting in to google analytics');
                }
            },
			onRoomStateChanged(room) {
				let roomState = room && room.state ? room.state : {}

				if (roomState.hasOwnProperty('currentPageIndex') && roomState.currentPageIndex >= 0) {
					this.$store.commit(MutationTypes.SET_CURRENT_PAGE_INDEX, roomState.currentPageIndex)
				}

				if (roomState.hasOwnProperty('pagePhase')) {
					this.$store.commit(MutationTypes.SET_CURRENT_PAGE_PHASE, roomState.pagePhase)
				}
			},
			onPhaseChanged(newPhase) {
				this.$store.commit(MutationTypes.SET_CURRENT_GAME_PHASE, newPhase)

				if (newPhase === GamePhase.Lobby) {
					this.logger.logMessage(`Going to Lobby as the GameClient said that's the current phase`)
					this.$store.dispatch({type: 'reset'})
					this.$router.push(getTeacherUrl('lobby'))
				} else if (newPhase === GamePhase.Pages) {
					this.logger.logMessage(`Going to Pages as the GameClient said that's the current phase`)
					this.$router.push(getTeacherUrl('pages'))
				} else if (newPhase === GamePhase.End) {
					this.$apiClient.postSession(this.$store.state.session, () => {
					}, () => {
					});
					this.$store.dispatch({type: 'reset'})
					this.$router.push(getTeacherUrl('lobby'))
				}
			},
			reloadCurrentPage(route) {
				let currentPage = route.params.page

				if (!currentPage || !components.hasOwnProperty(currentPage)) {
					this.logger.logMessage(`Unknown TeacherPage "${currentPage}"`)

					this.$router.push(getTeacherUrl('connect'))

					return
				}

				this.logger.logMessage(`TeacherPage changed to "${currentPage}"`)

				this.currentPage = currentPage
			}
		},
		mounted() {
			this.$store.commit(MutationTypes.SET_GAME_CLIENT_TYPE, GameClientType.Online);
			this.$store.commit(MutationTypes.SET_GAME_TYPE, GameType.ClassRoom);

			this.reloadCurrentPage(this.$router.currentRoute)

			$eBus.$on(GameClientEvent.RoomStateChange, this.onRoomStateChanged)
			$eBus.$on(GameClientEvent.PhaseChanged, this.onPhaseChanged)
		},
		beforeDestroy() {
			$eBus.$off(GameClientEvent.RoomStateChange, this.onRoomStateChanged)
			$eBus.$off(GameClientEvent.PhaseChanged, this.onPhaseChanged)
		}
	}
</script>
<style lang="less" scoped>
	.teacher-container {
		position: relative;
	}

	header {
		border-bottom: 1px solid #cccccc;
		padding-bottom: 10px;
		
		img {
			max-height: 50px;
		}
	}

	.teacher-page {
		position: absolute;
		top: 50px;
		left: 0;
		width: 100%;
		padding-top: 25px;
	}

	.room-code {
		position: fixed;
		right: 39px;
		bottom: 10px;
	}
</style>